import { FieldValues, useFormContext, useWatch } from 'react-hook-form'

import { ButtonDropdownOption } from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { BaseTransactionFormValues } from '~/components/smart-accounts/schemas'
import { createActionButtonProps } from '~/components/smart-accounts/transactions/BaseActionButtons'
import { TransactionMemoSection } from '~/components/smart-accounts/transactions/TransactionMemoSection'
import { TransactionReview } from '~/components/smart-accounts/transactions/TransactionReview'
import { createTransactionHandlers } from '~/components/smart-accounts/transactions/createTransactionHandlers'
import {
  OfframpDisplayData,
  StateChange,
} from '~/components/txn-display/state-change/types'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { useCanExecuteNewTransaction } from '~/hooks/useTransactions'
import { BasicTransactionType, IAddress } from '~/types'

interface TransactionDialogContentProps<TFormData extends FieldValues> {
  id: string
  title: string
  smartAccountAddress: IAddress
  addressesToWatch: IAddress[]
  isValidForm: boolean
  isLoading: boolean
  handlers: ReturnType<typeof createTransactionHandlers<TFormData>>
  chainId: number
  eventType: string
  children?: React.ReactNode
  afterMemo?: React.ReactNode
  reviewDetails?: React.ReactNode
  refreshData?: {
    dataUpdatedAt: number
    refetchInterval: number
    isLoading: boolean
  }
  bridgeStateChanges?: StateChange[]
  stateChangesToHide?: Record<IAddress, Record<IAddress, boolean>>
  transactionTypes: BasicTransactionType[]
  handleBack?: () => void
  offrampData?: OfframpDisplayData
  reviewDropdownOptions?: ButtonDropdownOption[]
}

export const TransactionDialogContent = <TFormData extends FieldValues>({
  title,
  id,
  isValidForm,
  isLoading,
  handlers,
  chainId,
  smartAccountAddress,
  addressesToWatch,
  eventType,
  children,
  afterMemo,
  reviewDetails,
  refreshData,
  bridgeStateChanges,
  stateChangesToHide,
  transactionTypes,
  handleBack: handleBackProp,
  offrampData,
  reviewDropdownOptions,
}: TransactionDialogContentProps<TFormData>) => {
  const { control, setValue } = useFormContext<BaseTransactionFormValues>()

  const isReview = useWatch({ control, name: 'isReview' }) ?? false
  const calls = useWatch({ control, name: 'calls' })

  const { smartAccounts } = useSmartAccounts()
  const smartAccount = smartAccounts.find(
    (smartAccount) => smartAccount.address === smartAccountAddress,
  )

  const handleReview = () => {
    setValue('isReview', true)
  }

  const handleBack = () => {
    if (handleBackProp) {
      handleBackProp()
    } else {
      setValue('isReview', false)
    }
  }

  const canExecute = useCanExecuteNewTransaction(smartAccount)

  const actionButtonProps = createActionButtonProps<TFormData>({
    canExecute,
    isValidForm: isValidForm,
    isLoading,
    handlers,
    eventType,
    isReview,
    handleReview,
    reviewDropdownOptions,
  })

  const { onExecute, onCreate } = handlers

  return (
    <SplitsDialogContent
      id={id}
      title={title}
      variant="cta"
      isLoading={isLoading}
      cancelButton={
        isReview
          ? {
              text: 'Back',
              onClick: handleBack,
              isDisabled: false,
            }
          : undefined
      }
      clampTitle={false}
      {...actionButtonProps}
    >
      <form onSubmit={canExecute ? onExecute : onCreate} className="space-y-4">
        {!isReview ? (
          <>
            {children}
            <TransactionMemoSection />
            {afterMemo}
          </>
        ) : (
          <TransactionReview
            smartAccountAddress={smartAccountAddress}
            chainId={chainId}
            calls={calls}
            addressesToWatch={addressesToWatch}
            bridgeStateChanges={bridgeStateChanges}
            refreshData={refreshData}
            stateChangesToHide={stateChangesToHide}
            transactionTypes={transactionTypes}
            offrampData={offrampData}
            reviewDetails={reviewDetails}
          />
        )}
      </form>
    </SplitsDialogContent>
  )
}
